import { ArticleBusinessType, InventoryPool } from 'admin/articleService'
import { ArticleFromApi, CategoryFromApi } from 'products/types'
import { getProductName } from 'products/productTable/utils'
import { CapacitySummary } from 'capacity/types'
import { countingTypeFromWire } from 'capacity/domain/countingType'
import { visitorKindOf } from 'capacity/api/clientSideGateway'
import { capacityPaths } from 'capacity/paths'
import { CONFIG, FORM_SECTION } from 'capacity/copy'
import { isPassType } from 'capacity/domain/productContext'

export interface SectionCapacityRow {
    key: string
    href: string
    name: string
    detail: string
    pending: boolean
}

export interface ProductSectionInput {
    accountSlug: string
    productId: string
    businessType: ArticleBusinessType | null
    isBundle: boolean
    usedResources: InventoryPool[]
    capacities: CapacitySummary[] | undefined
    categories?: CategoryFromApi[]
    window: 'slot' | 'day' | null
}

export const categoryName = (categories: CategoryFromApi[], id: string | undefined): string =>
    getProductName(categories.find((category) => category.id === id)?.name) || ''

// One row per link: a link scoped to a category is a separate charge, so it keeps its own row and says so.
export function productCapacityRows(input: ProductSectionInput): SectionCapacityRow[] {
    const { accountSlug, usedResources, capacities, categories = [], isBundle, window } = input
    const seen = new Set<string>()
    const rows: SectionCapacityRow[] = []
    const links = [...usedResources].sort((a, b) => Number(!!a.productListId) - Number(!!b.productListId))
    for (const link of links) {
        const key = `${link.poolId}:${link.productListId ?? ''}`
        if (seen.has(key)) {
            continue
        }
        seen.add(key)
        const capacity = capacities?.find((entry) => entry.id === link.poolId)
        const name = capacity?.name ?? FORM_SECTION.heading
        const href = capacityPaths.item(accountSlug, link.poolId)
        if (isBundle) {
            rows.push({ key, href, name, detail: FORM_SECTION.bundle, pending: false })
            continue
        }
        const scope = link.productListId
            ? [FORM_SECTION.onlyInCategory(categoryName(categories, link.productListId))]
            : []
        if (capacity && visitorKindOf(capacity)) {
            rows.push({
                key,
                href,
                name,
                detail: [FORM_SECTION.countsAsVisitors(link.amountUsed ?? 1), ...scope].join(' · '),
                pending: false,
            })
            continue
        }
        const parts = [FORM_SECTION.usesOfLimit(link.amountUsed ?? 1), ...scope]
        if (capacity) {
            parts.push(CONFIG.countsShort[countingTypeFromWire(capacity.reservationPeriodType)])
        }
        if (window) {
            parts.push(window === 'slot' ? FORM_SECTION.booksPerSlot : FORM_SECTION.booksWholeDay)
        }
        rows.push({ key, href, name, detail: parts.join(' · '), pending: !window })
    }
    return rows
}

// Inactive categories still count as membership: the product keeps their links and time slots.
export const articleCategoryIds = (article: Pick<ArticleFromApi, 'categories' | 'inactiveCategories'>): string[] =>
    [...article.categories, ...(article.inactiveCategories ?? [])].map((category) => category.id)

export const isInCategory = (article: ArticleFromApi, categoryId: string): boolean =>
    articleCategoryIds(article).includes(categoryId)

// Bundles and passes carry links the engine never charges, so they are not "products in a capacity".
export const usesCapacity = (article: ArticleFromApi): boolean =>
    article.resources.length > 0 && !article.isBundle && !isPassType(article.businessType)

export function categoryCapacityRows(
    accountSlug: string,
    categoryId: string,
    articles: ArticleFromApi[],
    capacities: CapacitySummary[] | undefined,
): SectionCapacityRow[] {
    const counts = new Map<string, number>()
    for (const article of articles) {
        if (!usesCapacity(article) || !isInCategory(article, categoryId)) {
            continue
        }
        new Set(article.resources.map((resource) => resource.inventoryPoolId)).forEach((poolId) =>
            counts.set(poolId, (counts.get(poolId) ?? 0) + 1),
        )
    }
    return [...counts.entries()]
        .map(([poolId, count]) => ({
            key: poolId,
            href: capacityPaths.item(accountSlug, poolId),
            name: capacities?.find((entry) => entry.id === poolId)?.name ?? FORM_SECTION.heading,
            detail: FORM_SECTION.categoryProducts(count),
            pending: false,
            count,
        }))
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
}
